import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, ArrowUpRight, RefreshCw, Unplug, Clock, History } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const Settings = () => {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [rates, setRates] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [history, setHistory] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, fx, sch, hist] = await Promise.all([
        api.get("/zoho/status"),
        api.get("/fx/rates"),
        api.get("/zoho/sync/schedule"),
        api.get("/zoho/sync/history?limit=10"),
      ]);
      setStatus(s.data);
      setRates(fx.data);
      setSchedule(sch.data);
      setHistory(hist.data.items || []);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const z = params.get("zoho");
    if (z === "connected") {
      toast.success("Connected to Zoho Books ✓");
      setParams({});
    } else if (z === "error") {
      toast.error(`Zoho connection failed: ${params.get("reason") || "unknown"}`);
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/auth/zoho/init");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error("Failed to start Zoho OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Zoho Books? You can reconnect any time.")) return;
    try {
      await api.post("/zoho/disconnect");
      toast.success("Disconnected");
      await load();
    } catch (e) {
      toast.error("Failed to disconnect");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#666] mb-2">
          Integrations · Configuration
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter">Settings</h1>
      </div>

      {/* Zoho */}
      <section className="bg-white border border-[#E5E5E5] p-6" data-testid="zoho-section">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">Zoho Books</h2>
              {status?.connected ? (
                <span className="inline-flex items-center gap-1.5 bg-[#047857] text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" data-testid="zoho-status-connected">
                  <CheckCircle2 className="w-3 h-3" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-[#F5F5F5] border border-[#E5E5E5] text-[#666] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" data-testid="zoho-status-disconnected">
                  <XCircle className="w-3 h-3" /> Disconnected
                </span>
              )}
            </div>
            <p className="text-sm text-[#666] mt-2 max-w-2xl">
              Pull customers, sales orders, invoices and payments from Zoho Books to compute contract value, balance to bill, outstanding receivables, and revenue.
            </p>
          </div>
          {status?.connected ? (
            <button
              onClick={disconnect}
              data-testid="disconnect-zoho-button"
              className="border border-[#E53E3E] text-[#E53E3E] px-4 py-2 text-sm font-medium hover:bg-[#E53E3E] hover:text-white transition-colors flex items-center gap-2"
            >
              <Unplug className="w-4 h-4" /> Disconnect
            </button>
          ) : (
            <button
              onClick={connect}
              disabled={connecting}
              data-testid="connect-zoho-button"
              className="bg-[#0A0A0A] text-white px-4 py-2 font-medium tracking-wide text-sm hover:bg-[#333] transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {connecting ? "Redirecting..." : "Connect to Zoho"}
              {!connecting && <ArrowUpRight className="w-4 h-4" />}
            </button>
          )}
        </div>

        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-[#E5E5E5]">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">Organization ID</dt>
            <dd className="text-sm font-mono">{status?.organization_id || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">API Domain</dt>
            <dd className="text-sm font-mono">{status?.api_domain || "https://www.zohoapis.com"}</dd>
          </div>
          <div className="md:col-span-2">
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">Redirect URI (must be registered in Zoho)</dt>
            <dd className="text-xs font-mono bg-[#F5F5F5] border border-[#E5E5E5] px-3 py-2 break-all" data-testid="redirect-uri">
              {status?.redirect_uri || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">Last Updated</dt>
            <dd className="text-sm font-mono">{status?.updated_at || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">Granted Scopes</dt>
            <dd className="text-xs font-mono break-all">{status?.scope || "—"}</dd>
          </div>
        </dl>

        {!status?.connected && (
          <div className="mt-4 bg-[#F5F5F5] border-l-[3px] border-l-[#D97706] p-3 text-xs font-mono text-[#666]">
            <strong className="text-[#0A0A0A]">Before clicking Connect:</strong> ensure the redirect URI shown above is registered in your Zoho Developer Console for client ID <span className="font-bold">1000.QEGKR5CVOFNI09Q3DJA7E8GQ719EUZ</span>.
          </div>
        )}
      </section>

      {/* Scheduled Sync */}
      <section className="bg-white border border-[#E5E5E5] p-6" data-testid="schedule-section">
        <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">Automatic Sync</h2>
              {schedule?.enabled ? (
                <span className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" data-testid="schedule-enabled">
                  <Clock className="w-3 h-3" /> Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-[#F5F5F5] border border-[#E5E5E5] text-[#666] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                  Inactive
                </span>
              )}
            </div>
            <p className="text-sm text-[#666] mt-2">
              Zoho Books is pulled automatically once a day. You can still trigger a manual sync any time from the Dashboard.
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-[#E5E5E5]">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">Cadence</dt>
            <dd className="text-sm font-mono">Daily</dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">Run Time (UTC)</dt>
            <dd className="text-sm font-mono" data-testid="schedule-time">{schedule?.time_utc || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-1">Next Run</dt>
            <dd className="text-sm font-mono" data-testid="schedule-next-run">
              {schedule?.next_run ? new Date(schedule.next_run).toLocaleString() : "—"}
            </dd>
          </div>
        </dl>

        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-[#666]" />
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#666]">Recent Sync History</h3>
          </div>
          <div className="border border-[#E5E5E5]" data-testid="sync-history-table">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#F5F5F5] border-b border-[#0A0A0A]">
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">When</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Trigger</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Result</th>
                  <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Duration</th>
                  <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-[#666] py-2 px-3">Records</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-xs text-[#666] py-6 font-mono">No syncs yet</td></tr>
                ) : (
                  history.map((h, i) => (
                    <tr key={i} className="border-b border-[#E5E5E5] last:border-b-0">
                      <td className="py-2 px-3 text-xs font-mono">{new Date(h.started_at).toLocaleString()}</td>
                      <td className="py-2 px-3 text-xs">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${h.trigger === "scheduled" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] border border-[#E5E5E5]"}`}>
                          {h.trigger}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs font-mono">
                        {h.ok ? (
                          <span className="text-[#047857] font-semibold">OK</span>
                        ) : (
                          <span className="text-[#E53E3E]" title={h.error}>FAIL{h.error ? ` · ${h.error}` : ""}</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs font-mono text-right">{h.duration_sec ? `${h.duration_sec}s` : "—"}</td>
                      <td className="py-2 px-3 text-xs font-mono text-right">
                        {h.counts ? `${h.counts.invoices} inv · ${h.counts.salesorders} SO · ${h.counts.payments} pay` : "—"}
                        {h.auto_import && (h.auto_import.created > 0 || h.auto_import.updated > 0 || h.auto_import.invoices_linked > 0) && (
                          <div className="text-[10px] text-[#B8860B] mt-0.5">
                            Auto-link: +{h.auto_import.created} new, {h.auto_import.invoices_linked} linked
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FX rates */}
      <section className="bg-white border border-[#E5E5E5] p-6" data-testid="fx-section">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Currency Conversion</h2>
            <p className="text-sm text-[#666] mt-2">Base currency: USD. Rates refreshed automatically every 6 hours from frankfurter.app (free).</p>
          </div>
          <button
            onClick={load}
            data-testid="refresh-fx"
            className="border border-[#0A0A0A] px-3 py-2 text-sm font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-4 border-t border-[#E5E5E5]">
          {rates && Object.entries(rates.rates_to_usd)
            .filter(([c]) => ["USD", "INR", "AED", "EUR", "GBP", "SGD", "CAD", "AUD"].includes(c))
            .map(([code, rate]) => (
              <div key={code} className="bg-[#F5F5F5] p-3" data-testid={`fx-${code}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#666]">1 {code} =</div>
                <div className="text-lg font-mono font-medium mt-1">${Number(rate).toFixed(4)}</div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
};

export default Settings;
